/**
 * Stage J — 3D Model & Close-Out panel.
 *   J.1  3D Model & Clash        → J1Model
 *   J.2  Design Review           → J2Review
 *   J.3  Deliverables            → J3Deliverables
 *   J.4  Close-Out               → J4Closeout
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";

const _fmt = (n, d = 1) => (n == null ? "-"
    : typeof n === "number" ? (Number.isInteger(n) ? n : n.toFixed(d)) : n);

function StatTile({ label, value, testid }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/60 p-3"
          data-testid={testid}>
      <div className="text-2xl font-bold text-neutral-100">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4"
          data-testid={testid}>
      <div className="mb-2 text-sm font-semibold text-neutral-200">{title}</div>
      {children}
    </div>
  );
}

function Table({ cols, rows, testid }) {
  return (
    <div className="overflow-x-auto rounded border border-neutral-800"
          data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-neutral-900 text-neutral-400">
          <tr>{cols.map(([, l]) => <th key={l} className="px-2 py-2 text-left font-medium">{l}</th>)}</tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={i} className="border-t border-neutral-800 text-neutral-200">
              {cols.map(([k]) => <td key={k} className="px-2 py-1.5">{_fmt(r?.[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

async function _download(projectId, path, payload, filename, ct) {
  const r = await api.post(`/projects/${projectId}${path}`, payload || {});
  const blob = new Blob([r.data], { type: ct });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  a.click(); URL.revokeObjectURL(url);
}

/* ═══════════════════ J.1 · 3D Model ═══════════════════════════ */
function J1Model({ projectId, res }) {
  const m = res?.consolidated_model_summary || {};
  const c = res?.clashes || {};
  const vps = res?.viewpoints || [];
  const s = res?.model_statistics || {};
  return (
    <div className="space-y-4" data-testid="j1-model">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Model elements" value={m.total_elements || 0}
                    testid="j1-tile-elements" />
        <StatTile label="Clashes total" value={c.n_total || 0}
                    testid="j1-tile-clashes" />
        <StatTile label="Hard clashes"
                    value={c.by_severity?.hard || 0}
                    testid="j1-tile-hard" />
        <StatTile label="Quality score"
                    value={`${s.model_quality?.quality_score_pct || 0}%`}
                    testid="j1-tile-quality" />
      </div>

      <Section title="Consolidated 3D model — 10 discipline layers (J.1-F1)"
                  testid="j1-layers">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-neutral-300">
          {Object.entries(m.layers || {}).map(([k, n]) => (
            <div key={k}>{k.replace(/_/g, " ")}: <b>{n}</b></div>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-neutral-500">
          Bounding box: {JSON.stringify(m.bounding_box)}
        </div>
      </Section>

      <Section title={`Multi-discipline clash detection · ${c.n_total} clashes (J.1-F3)`}
                  testid="j1-clashes">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-neutral-300 mb-2">
          {Object.entries(c.by_pair || {}).map(([k, n]) => (
            <div key={k}>{k.replace(/_/g, " ")}: <b>{n}</b></div>
          ))}
        </div>
        <Table cols={[
          ["id", "ID"], ["pair_type", "Pair"], ["severity", "Severity"],
          ["responsible", "Responsible"], ["recommendation", "Fix"],
        ]} rows={(c.clashes || []).slice(0, 15)} />
      </Section>

      <Section title={`Review viewpoints · ${vps.length} saved (J.1-F4)`}
                  testid="j1-viewpoints">
        <Table cols={[
          ["id", "ID"], ["name", "Name"],
          ["discipline", "Discipline"], ["annotation", "Notes"],
        ]} rows={vps} />
      </Section>

      <Section title="Model statistics (J.1-F5)" testid="j1-statistics">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-neutral-300">
          {Object.entries(s.counts || {}).map(([k, v]) => (
            <div key={k}>{k.replace(/_/g, " ")}: <b>{v}</b></div>
          ))}
        </div>
        <div className="mt-3 border-t border-neutral-800 pt-2">
          <div className="text-xs font-semibold text-neutral-300 mb-1">Weights (kg)</div>
          {Object.entries(s.weights_kg || {}).map(([k, v]) => (
            <div key={k} className="text-[11px] text-neutral-400">{k.replace(/_/g, " ")}: <b>{_fmt(v, 0)}</b></div>
          ))}
        </div>
        <div className="mt-3 border-t border-neutral-800 pt-2">
          <div className="text-xs font-semibold text-neutral-300 mb-1">Quality issues</div>
          {Object.entries(s.model_quality || {}).map(([k, v]) => (
            <div key={k} className="text-[11px] text-neutral-400">{k.replace(/_/g, " ")}: <b>{v}</b></div>
          ))}
        </div>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs" data-testid="j1-ifc-btn"
                  onClick={() => _download(projectId, "/stage-j/ifc4",
                    { consolidated_model: {}, project_info: {} },
                    "model.ifc", "application/x-step")}>
          Download IFC4
        </button>
        <button className="btn btn-secondary text-xs" data-testid="j1-bim-btn"
                  onClick={() => _download(projectId, "/stage-j/bim-bundle",
                    { consolidated: {}, project_info: {} },
                    "bim-bundle.zip", "application/zip")}>
          Download BIM bundle (ZIP)
        </button>
        <button className="btn btn-secondary text-xs" data-testid="j1-bcf-btn"
                  onClick={() => _download(projectId, "/stage-j/bcf",
                    { clashes: c.clashes || [], viewpoints: vps,
                       project_name: res?.project_name || "Capexara" },
                    "issues.bcf", "application/zip")}>
          Download BCF (issues)
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ J.2 · Design Review ══════════════════════ */
function J2Review({ res }) {
  const ch = res?.checklist_60pct || {};
  const at = res?.action_tracker || {};
  const wd = res?.pid_walkdown || {};
  const hv = res?.hazop_verification || {};
  const idc = res?.idc_matrix || {};
  const report = res?.design_review_report || {};
  return (
    <div className="space-y-4" data-testid="j2-review">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Action items open"
                    value={at.counts?.open || 0} testid="j2-tile-open" />
        <StatTile label="Walkdown findings"
                    value={wd.n_findings || 0} testid="j2-tile-findings" />
        <StatTile label="HAZOP compliance"
                    value={`${hv.compliance_pct || 0}%`}
                    testid="j2-tile-hazop" />
        <StatTile label="Recommendation"
                    value={report.recommendation || "-"}
                    testid="j2-tile-recommend" />
      </div>

      <Section title={`60% Review Checklist — ${ch.name} (J.2-F1)`}
                  testid="j2-checklist">
        <div className="text-xs text-neutral-300 mb-2">
          Focus: <b>{ch.review_focus}</b>
        </div>
        <ul className="text-xs text-neutral-400 space-y-1 list-disc pl-4">
          {(ch.deliverables || []).map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      </Section>

      <Section title={`Action items tracker · ${at.counts?.total} items (J.2-F2)`}
                  testid="j2-actions">
        <Table cols={[
          ["action_id", "ID"], ["discipline", "Discipline"],
          ["priority", "P"], ["assigned_to", "Assigned"],
          ["target_date", "Target"], ["status", "Status"],
        ]} rows={(at.items || []).slice(0, 12)} />
      </Section>

      <Section title={`P&ID walkdown vs 3D model (J.2-F3) · ${wd.walkdown_status}`}
                  testid="j2-walkdown">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>P&amp;ID equipment: <b>{wd.pid_equipment_count}</b> · Model equipment: <b>{wd.model_equipment_count}</b></div>
          <div className="mt-2"><b>Findings:</b></div>
          {(wd.findings || []).slice(0, 6).map((f, i) => (
            <div key={i} className="text-[11px] text-amber-300">- {f.tag}: {f.issue} ({f.severity})</div>
          ))}
        </div>
      </Section>

      <Section title="HAZOP P&ID verification (J.2-F4)" testid="j2-hazop">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>HAZOP actions: <b>{hv.hazop_actions_total}</b> · Verified: <b>{hv.verified_on_pid}</b> · Unimplemented: <b>{hv.n_unimplemented}</b></div>
          <div>Compliance: <b className={hv.compliance_pct === 100 ? "text-green-400" : "text-amber-400"}>{hv.compliance_pct}%</b></div>
          {(hv.unimplemented || []).map((u, i) => (
            <div key={i} className="text-[11px] text-red-300">- {u.id}: {u.action}</div>
          ))}
        </div>
      </Section>

      <Section title={`Interdiscipline Check matrix · ${idc.n_pairs} pairs, ${idc.total_checks} checks (J.2-F5)`}
                  testid="j2-idc">
        <div className="space-y-2 text-xs">
          {(idc.pairs || []).map((p, i) => (
            <div key={i} className="border-l-2 border-neutral-700 pl-2">
              <b className="text-neutral-200">{p.from} → {p.to}</b>
              <ul className="text-[11px] text-neutral-400 list-disc pl-4">
                {(p.checks || []).map((c, j) => <li key={j}>{c}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Design review report · ${report.cover?.milestone} (J.2-F6)`}
                  testid="j2-report">
        <div className="text-xs text-neutral-300 space-y-1">
          {(report.sections || []).map(s => (
            <div key={s.no} className="border-l-2 border-neutral-700 pl-2">
              <b>§{s.no} {s.title}</b>: {s.content}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

/* ═══════════════════ J.3 · Deliverables ═══════════════════════ */
function J3Deliverables({ projectId, res }) {
  const reg = res?.drawing_register_first10 || [];
  const idx = res?.document_index_first10 || [];
  const rev = res?.sample_revision_workflow || {};
  const summary = res?.project_summary || {};
  const trn = res?.sample_transmittal || {};
  return (
    <div className="space-y-4" data-testid="j3-deliverables">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Drawings"
                    value={res?.drawing_register_count || 0}
                    testid="j3-tile-drawings" />
        <StatTile label="Documents"
                    value={res?.document_index_count || 0}
                    testid="j3-tile-docs" />
        <StatTile label="Equipment count"
                    value={summary.equipment_count || 0}
                    testid="j3-tile-equipment" />
        <StatTile label="Steel tonnage"
                    value={`${summary.steel_tonnage_t || 0} t`}
                    testid="j3-tile-steel" />
      </div>

      <Section title={`Master drawing register · ${res?.drawing_register_count} drawings (J.3-F1)`}
                  testid="j3-drawing-reg">
        <Table cols={[
          ["doc_no", "Doc #"], ["title", "Title"],
          ["discipline", "Discipline"], ["revision", "Rev"],
          ["status", "Status"], ["sheets", "Sheets"],
          ["reference", "Ref"],
        ]} rows={reg} />
      </Section>

      <Section title={`Master document index · ${res?.document_index_count} documents (J.3-F2)`}
                  testid="j3-doc-index">
        <Table cols={[
          ["doc_no", "Doc #"], ["title", "Title"],
          ["discipline", "Discipline"], ["type", "Type"],
          ["revision", "Rev"], ["status", "Status"],
          ["reference", "Ref"],
        ]} rows={idx} />
      </Section>

      <Section title="Revision workflow demo (J.3-F3)" testid="j3-revision">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Doc: <b>{rev.doc_no}</b></div>
          <div>{rev.previous_state} Rev {rev.previous_revision} → <b className="text-green-400">{rev.new_state} Rev {rev.new_revision}</b></div>
          <div>Workflow: {(rev.workflow_states || []).join(" → ")}</div>
          <div className={rev.frozen ? "text-amber-400" : "text-neutral-400"}>
            Frozen: {String(rev.frozen)} {rev.moc_required && "· MOC required"}
          </div>
        </div>
      </Section>

      <Section title={`Project summary (J.3-F5)`} testid="j3-summary">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-neutral-300">
          <div>Project: <b>{summary.project_name}</b></div>
          <div>Location: <b>{summary.location}</b></div>
          <div>Plant type: <b>{summary.plant_type}</b></div>
          <div>Capacity: <b>{summary.capacity}</b></div>
          <div>Equipment: <b>{summary.equipment_count}</b></div>
          <div>Piping: <b>{summary.total_piping_length_m} m</b></div>
          <div>Steel: <b>{summary.steel_tonnage_t} t</b></div>
          <div>Est. cost: <b>${_fmt(summary.estimated_cost?.total, 0)}</b></div>
          <div>Codes: <b>{summary.applicable_codes}</b></div>
        </div>
      </Section>

      <Section title={`Sample transmittal · ${trn.transmittal_no} (J.3-F6)`}
                  testid="j3-transmittal">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>From: <b>{trn.from}</b> → To: <b>{trn.to}</b></div>
          <div>Purpose: <b>{trn.purpose}</b> · Documents: <b>{(trn.documents || []).length}</b></div>
          <div className="mt-1 text-[11px] text-neutral-500">{trn.remarks}</div>
        </div>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-primary text-xs" data-testid="j3-pkg-btn"
                  onClick={() => _download(projectId,
                    "/stage-j/deliverables-package",
                    { project_info: {}, drawing_register: reg,
                       document_index: idx },
                    "deliverables.zip", "application/zip")}>
          Download deliverables ZIP (10-folder structure)
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ J.4 · Close-Out ═══════════════════════════ */
function J4Closeout({ res }) {
  const ab = res?.as_built || {};
  const pl = res?.punch_list || {};
  const co = res?.commissioning_first5 || [];
  const ho = res?.handover_package || {};
  const ll = res?.lessons_learned || {};
  const complete = res?.project_completeness || {};
  return (
    <div className="space-y-4" data-testid="j4-closeout">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Punch list total"
                    value={pl.counts?.total || 0}
                    testid="j4-tile-punch" />
        <StatTile label="Cat A (safety)"
                    value={pl.counts?.A || 0}
                    testid="j4-tile-cat-a" />
        <StatTile label="Handover sets"
                    value={(ho.contents || []).length}
                    testid="j4-tile-handover" />
        <StatTile label="Project"
                    value={complete.project_status || "-"}
                    testid="j4-tile-project" />
      </div>

      <Section title="As-built documentation workflow (J.4-F1)"
                  testid="j4-as-built">
        <div className="text-xs text-neutral-300 mb-2">
          Drawings: <b>{ab.n_total_drawings}</b> · Field changes: <b>{ab.n_field_changes}</b> · Updated to as-built: <b>{ab.n_updated_to_as_built}</b> ({ab.as_built_pct}%)
        </div>
        <ol className="text-[11px] text-neutral-400 list-decimal pl-4">
          {(ab.workflow_steps || []).map((s, i) => <li key={i}>{s.replace(/^\d+\.\s*/, '')}</li>)}
        </ol>
      </Section>

      <Section title={`Punch list template (J.4-F2) · A/B/C categories`}
                  testid="j4-punch-list">
        <div className="grid grid-cols-3 gap-2 text-xs text-neutral-300 mb-2">
          <div className="text-red-400">A (safety): <b>{pl.counts?.A}</b></div>
          <div className="text-amber-400">B (operational): <b>{pl.counts?.B}</b></div>
          <div className="text-neutral-400">C (cosmetic): <b>{pl.counts?.C}</b></div>
        </div>
        <Table cols={[
          ["item_no", "Item"], ["equipment", "Equipment"],
          ["category", "Cat"], ["description", "Description"],
          ["status", "Status"],
        ]} rows={(pl.items_first10 || [])} />
      </Section>

      <Section title={`Commissioning checklists · ${res?.commissioning_count} systems (J.4-F3)`}
                  testid="j4-commissioning">
        <div className="space-y-3 text-xs">
          {co.map((c, i) => (
            <div key={i} className="border-l-2 border-neutral-700 pl-2">
              <b className="text-neutral-200">{c.equipment_tag} ({c.equipment_type}) · {c.n_checks} checks</b>
              <ul className="text-[11px] text-neutral-400 list-disc pl-4">
                {(c.checks || []).slice(0, 4).map((chk, j) => <li key={j}>{chk.check}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Handover package (J.4-F4) · ${(ho.contents || []).length} document sets`}
                  testid="j4-handover">
        <Table cols={[
          ["no", "#"], ["set", "Document set"],
          ["reference", "Reference"], ["included", "Included"],
        ]} rows={ho.contents || []} />
        <div className="mt-2 text-[11px] text-neutral-400">
          Digital handover: <b>{ho.digital_handover?.format}</b> · ~{ho.digital_handover?.size_estimate_mb} MB
        </div>
      </Section>

      <Section title="Lessons learned (J.4-F5)" testid="j4-lessons">
        <div className="text-xs text-neutral-300 mb-2">
          Categories: {(ll.categories || []).join(" · ")}
        </div>
        {(ll.sample_lessons || []).map((l, i) => (
          <div key={i} className="border-l-2 border-neutral-700 pl-2 mb-2 text-[11px]">
            <b className="text-neutral-200">{l.id} · {l.category}</b>
            <div className="text-neutral-400">What: {l.what_happened}</div>
            <div className="text-neutral-400">Root cause: {l.root_cause}</div>
            <div className="text-green-400">→ {l.recommendation}</div>
          </div>
        ))}
      </Section>

      <Section title="PROJECT COMPLETION (J.4-F6)" testid="j4-project-complete">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Status: <b className={complete.complete ? "text-green-400" : "text-amber-400"}>{complete.project_status}</b> ({complete.percentage}%)</div>
          <div>Pass: {complete.n_pass} / {complete.n_total}</div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-1 text-[11px]">
            {Object.entries(complete.by_stage || {}).map(([stg, s]) => (
              <div key={stg} className={s.pct === 100 ? "text-green-400" : "text-amber-400"}>
                {stg}: {s.pass}/{s.total} ({s.pct}%)
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}

/* ═════════════════════ Wrapper ════════════════════════════════ */
export default function StageJPanel({ projectId, subStage }) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post(`/projects/${projectId}/stage-j/run`, {});
      setRes(r.data);
    } catch (e) {
      setRes({ error: e.message });
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { run(); }, [run]);

  return (
    <div className="space-y-4" data-testid={`stage-j-panel-${subStage}`}>
      <div className="flex items-center justify-between rounded-md border border-neutral-800 bg-neutral-950 p-3">
        <div>
          <div className="text-sm font-semibold text-neutral-100">Stage J · 3D Model & Close-Out (FINAL)</div>
          <div className="text-xs text-neutral-400">
            Sub-stage {subStage} · powered by J.1-J.4 gap-closure engines (26 fixes)
          </div>
        </div>
        <button onClick={run} className="btn btn-primary text-xs"
                  data-testid="stage-j-rerun-btn">Re-run pipeline</button>
      </div>

      {loading && (
        <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-500"
              data-testid="stage-j-loading">
          Running Stage J pipeline · consolidating 3D · IFC4 export · clash detection · project completion…
        </div>
      )}

      {!loading && res?.error && (
        <div className="rounded-md border border-red-900 bg-red-950/50 p-4 text-xs text-red-300"
              data-testid="stage-j-error">
          Pipeline error: {res.error}
        </div>
      )}

      {!loading && !res?.error && (
        <>
          {subStage === "J.1" && <J1Model         projectId={projectId} res={res} />}
          {subStage === "J.2" && <J2Review        res={res} />}
          {subStage === "J.3" && <J3Deliverables  projectId={projectId} res={res} />}
          {subStage === "J.4" && <J4Closeout      res={res} />}
        </>
      )}
    </div>
  );
}
